// ── Resolve a pasted SharePoint library address into the identifiers VeriGRC stores ──────────────
//
// The admin pastes the address of their Evidence library as it appears in the browser. This turns it
// into { site, drive, list, directory } using THE ADMIN'S delegated session (see ./delegated-graph).
//
// ⚠ NEVER GUESSES A LIBRARY. A site address with no library in it is an error that asks for the library
//   address — not a silent fall back to the site's default "Documents", which would bind the wrong
//   library and grant nothing the admin intended.
//
// ⚠ Three reads, all with the admin's permissions:
//     GET /sites/{host}:/{sites|teams}/{name}      → the site
//     GET /sites/{siteId}/drives                   → the library whose address matches the pasted one
//     GET /drives/{driveId}/list                   → the list id the admin's grant will be keyed to
//   LIVE-VERIFY: all three, against a real tenant, with a real delegated token.

import { DelegatedGraphError, type DelegatedGraphClient } from './delegated-graph';

export interface ResolvedEvidenceLibrary {
  entraTenantId: string;
  siteId: string;
  siteName: string;
  driveId: string;
  listId: string;
  libraryName: string;
  libraryWebUrl: string;
}

export type ResolveErrorCode =
  | 'invalid_url'
  | 'not_sharepoint'
  | 'sharing_link'
  | 'library_not_in_url'
  | 'site_not_found'
  | 'library_not_found'
  | 'access_denied'
  | 'provider_error';

export class ResolveLibraryError extends Error {
  constructor(readonly code: ResolveErrorCode, message: string, readonly availableLibraries: string[] = []) {
    super(message);
    this.name = 'ResolveLibraryError';
  }
}

export interface ParsedLibraryUrl {
  host: string;
  /** '' for the root site, else e.g. 'sites/GRC'. */
  sitePath: string;
  /** The library's URL segment, decoded — e.g. 'Compliance Evidence'. */
  librarySegment: string;
}

/** Pure: read host, site path and library segment out of a pasted address. */
export function parseLibraryUrl(raw: string): ParsedLibraryUrl {
  let url: URL;
  try { url = new URL(raw.trim()); } catch {
    throw new ResolveLibraryError('invalid_url', 'That is not a web address. Paste the address of your Evidence library from the browser.');
  }
  if (url.protocol !== 'https:' || !/\.sharepoint\.com$/i.test(url.hostname)) {
    throw new ResolveLibraryError('not_sharepoint', 'That address is not a SharePoint address. It should look like https://yourcompany.sharepoint.com/sites/…');
  }
  const segments = url.pathname.split('/').filter(Boolean).map((s) => decodeURIComponent(s));
  // Sharing links (/:f:/s/…, /:x:/r/…) are opaque tokens, not library addresses.
  if (segments[0]?.startsWith(':')) {
    throw new ResolveLibraryError('sharing_link', 'That is a sharing link. Open the library in SharePoint and paste the address from the browser instead.');
  }

  let sitePath = '';
  let rest = segments;
  if (segments[0] === 'sites' || segments[0] === 'teams') {
    if (!segments[1]) throw new ResolveLibraryError('invalid_url', 'That address names no site. Paste the address of the library itself.');
    sitePath = `${segments[0]}/${segments[1]}`;
    rest = segments.slice(2);
  }
  const librarySegment = rest[0];
  if (!librarySegment || /\.aspx$/i.test(librarySegment) || librarySegment === 'SitePages' || librarySegment === '_layouts') {
    throw new ResolveLibraryError('library_not_in_url',
      'That is the address of a site, not of a library. Open your Evidence library and paste its address.');
  }
  return { host: url.hostname.toLowerCase(), sitePath, librarySegment };
}

const pathOf = (webUrl: string) => {
  try { return decodeURIComponent(new URL(webUrl).pathname).replace(/\/+$/, '').toLowerCase(); } catch { return ''; }
};

function translate(err: unknown, what: 'site' | 'library'): never {
  if (err instanceof ResolveLibraryError) throw err;
  if (err instanceof DelegatedGraphError) {
    if (err.status === 401 || err.status === 403) {
      throw new ResolveLibraryError('access_denied', 'Your Microsoft account cannot open that site or library. Sign in as an administrator who can.');
    }
    if (err.status === 404) {
      throw new ResolveLibraryError(what === 'site' ? 'site_not_found' : 'library_not_found',
        what === 'site' ? 'No SharePoint site was found at that address.' : 'That library was not found on the site.');
    }
    throw new ResolveLibraryError('provider_error', `Microsoft 365 returned an error (${err.status}).`);
  }
  throw err;
}

export async function resolveEvidenceLibrary(raw: string, client: DelegatedGraphClient): Promise<ResolvedEvidenceLibrary> {
  const parsed = parseLibraryUrl(raw);

  let site: { id: string; displayName?: string; webUrl?: string };
  try {
    site = await client.get(parsed.sitePath
      ? `/sites/${parsed.host}:/${parsed.sitePath}?$select=id,displayName,webUrl`
      : `/sites/${parsed.host}?$select=id,displayName,webUrl`);
  } catch (err) { translate(err, 'site'); }

  let drives: { value: { id: string; name?: string; webUrl?: string }[] };
  try {
    drives = await client.get(`/sites/${site.id}/drives?$select=id,name,webUrl`);
  } catch (err) { translate(err, 'library'); }

  // ⚠ MATCHED BY ADDRESS, NOT BY NAME. A library's display name and its URL segment can differ (a renamed
  //   "Shared Documents" still lives at /Shared Documents), and two libraries can share a display name.
  const want = `/${parsed.sitePath ? `${parsed.sitePath}/` : ''}${parsed.librarySegment}`.toLowerCase();
  const drive = drives.value.find((d) => d.webUrl && pathOf(d.webUrl) === want);
  if (!drive) {
    throw new ResolveLibraryError('library_not_found',
      `No library at that address on "${site.displayName ?? 'this site'}".`,
      drives.value.map((d) => d.name ?? d.id));
  }

  let list: { id: string };
  try {
    list = await client.get(`/drives/${drive.id}/list?$select=id`);
  } catch (err) { translate(err, 'library'); }

  return {
    entraTenantId: client.tenantId,
    siteId: site.id,
    siteName: site.displayName ?? parsed.sitePath,
    driveId: drive.id,
    listId: list.id,
    libraryName: drive.name ?? parsed.librarySegment,
    libraryWebUrl: drive.webUrl as string,
  };
}
